import { ref, onMounted, onUnmounted } from 'vue'
import { Keyboard } from '@capacitor/keyboard'

/**
 * Composable for iOS keyboard avoidance in sheets/modals.
 * Listens for keyboard show/hide events via Capacitor Keyboard plugin,
 * falls back to visualViewport resize detection in the browser.
 * Returns reactive keyboardHeight and a computed style object for padding.
 */
export function useKeyboardAvoidance() {
  const keyboardHeight = ref(0)
  let cleanup = null

  const scrollFocusedElementIntoView = () => {
    const el = document.activeElement
    if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA')) {
      setTimeout(() => {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }, 100)
    }
  }

  const contentStyle = () => {
    if (keyboardHeight.value > 0) {
      return { paddingBottom: `${keyboardHeight.value}px` }
    }
    return {}
  }

  onMounted(async () => {
    try {
      // Capacitor Keyboard plugin (device / simulator)
      await Keyboard.addListener('keyboardWillShow', (info) => {
        keyboardHeight.value = info.keyboardHeight
        scrollFocusedElementIntoView()
      })

      await Keyboard.addListener('keyboardWillHide', () => {
        keyboardHeight.value = 0
      })

      cleanup = async () => {
        try {
          await Keyboard.removeAllListeners()
        } catch (_) {
          // plugin unavailable
        }
      }
    } catch (_) {
      // Fallback: visualViewport resize (browser / PWA)
      const initialHeight =
        window.visualViewport?.height || window.innerHeight

      const handleViewportChange = () => {
        const current = window.visualViewport?.height || window.innerHeight
        const diff = initialHeight - current

        if (diff > 150) {
          keyboardHeight.value = diff
          scrollFocusedElementIntoView()
        } else {
          keyboardHeight.value = 0
        }
      }

      if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', handleViewportChange)
        cleanup = () =>
          window.visualViewport.removeEventListener(
            'resize',
            handleViewportChange
          )
      } else {
        window.addEventListener('resize', handleViewportChange)
        cleanup = () =>
          window.removeEventListener('resize', handleViewportChange)
      }
    }
  })

  onUnmounted(async () => {
    if (cleanup) await cleanup()
  })

  return { keyboardHeight, contentStyle }
}
